import React, { useState, useRef, useEffect } from 'react';
import './index.scss';
export const SearchableSelect = ({
  options = [],
  value = null,
  onChange,
  placeholder = 'State',
  className = '',
  isDisabled = false,
  selectHandler
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasChangedSinceSelect, setHasChangedSinceSelect] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const [filteredOptions, setFilterOptions] = useState(options);
  const itemRefs = useRef([]);

  useEffect(() => {
    if (value && !hasChangedSinceSelect) {
      setSearchTerm(value.value);
    }
  }, [value]);

  useEffect(() => {
    if (highlightedIndex >= 0 && itemRefs.current[highlightedIndex]) {
      itemRefs.current[highlightedIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [highlightedIndex]);

  const handleSelect = option => {
    onChange(option);
    setSearchTerm(option.label);
    setHasChangedSinceSelect(false);
    setIsOpen(false);
    selectHandler(option.label);
  };

  const handleInputChange = e => {
    const newValue = e.target.value;
    if (newValue.length > 2) return;
    setSearchTerm(newValue);
    setHasChangedSinceSelect(true);
    setIsOpen(true);
    setHighlightedIndex(-1);
    if (value && newValue !== value.label) {
      onChange(null);
    }
    if (newValue.length > 0) {
      setFilterOptions(
        options.filter(option =>
          option.label.toLowerCase().includes(newValue.toLowerCase())
        )
      );
    } else {
      setFilterOptions(options);
    }
    selectHandler(newValue);
  };

  const handleKeyDown = e => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setIsOpen(true);
      setHighlightedIndex(0);
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );

        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (
          highlightedIndex >= 0 &&
          highlightedIndex < filteredOptions.length
        ) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  useEffect(() => {
    const handleOutsideClick = e => {
      if (!wrapperRef.current?.contains(e.target)) {
        closeDropdown();
      }
    };
    const handleFocusChange = e => {
      if (!wrapperRef.current?.contains(e.target)) {
        closeDropdown();
      }
    };
    const closeDropdown = () => {
      setIsOpen(false);
      setHighlightedIndex(-1);
      if (!searchTerm.trim() && hasChangedSinceSelect) {
        onChange(null);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('focusin', handleFocusChange);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('focusin', handleFocusChange);
    };
  }, [searchTerm]);

  return (
    <div className={`relative w-full ${className}`} ref={wrapperRef}>
      <div className="dropRelative">
        <input
          ref={inputRef}
          type="text"
          disabled={isDisabled}
          className="stateDropdown themeFocus dialogComponent"
          placeholder={placeholder}
          value={
            !hasChangedSinceSelect && value?.value ? value.value : searchTerm
          }
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onClick={() => setIsOpen(!isOpen)}
          onFocus={e => {
            if (value) return;
            setIsOpen(isOpen);
            const val = e.target.value;
            e.target.setSelectionRange(val.length, val.length);
            if (filteredOptions.length > 0) {
              setHighlightedIndex(0);
            }
          }}
        />
      </div>
      {isOpen && !isDisabled && (
        <div className="mainDropDown">
          <div className="selectDropDown">
            {filteredOptions.map((option, index) => (
              <div
                key={option.value}
                ref={el => (itemRefs.current[index] = el)}
                className={`stateList ${
                  value?.value === option.value ? 'selected' : ''
                } ${index === highlightedIndex ? 'current' : ''}`}
                onMouseDown={() => handleSelect(option)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <span>{option.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
